import { z } from 'zod';

/**
 * Las industrias que puede tener una MARCA.
 *
 * Se eligen de una lista y no se escriben: con campo libre acaban conviviendo
 * «F&B», «Food and Beverage» y «bebidas», y entonces agrupar marcas por
 * industria —que es para lo único que sirve el dato— deja de funcionar.
 * Añadir una entrada aquí es seguro; renombrarla deja huérfanas las marcas ya
 * guardadas con el texto anterior, así que se añade la nueva y se retira la
 * vieja en una segunda pasada.
 */
const INDUSTRIES = [
  'Food & Beverage',
  'Beer, Wine & Spirits',
  'Retail',
  'Beauty & Personal Care',
  'Health & Wellness',
  'Fashion & Apparel',
  'Tech & Electronics',
  'Automotive',
  'Entertainment & Media',
  'Sports & Fitness',
  'Travel & Hospitality',
  'Financial Services',
  'Home & Living',
  'Pets',
  'Other',
] as const;
export const industries = INDUSTRIES;

/**
 * Qué es la cuenta que se está dando de alta.
 *
 * Sustituye a la industria en el alta: la industria no decidía nada y esto sí
 * —una agencia ve el stock de toda su cartera— y además es lo primero que hay
 * que saber para hablarle a cada quien de lo suyo.
 */
export const COMPANY_KINDS = ['Agency', 'Company', 'Property'] as const;

export const brandProfileSchema = z.object({
  companyName: z.string().min(2, 'Company name is required'),
  contactName: z.string().min(2, 'Contact name is required'),
  companyType: z.enum(COMPANY_KINDS, { message: 'Please pick one' }),
});
export type BrandProfileInput = z.infer<typeof brandProfileSchema>;

const HEX = /^#[0-9a-fA-F]{6}$/;

export const brandKitSchema = z.object({
  campaignName: z.string().min(2, 'Campaign name is required'),
  primaryColor: z.string().regex(HEX, 'Use a HEX color like #B262F5'),
  secondaryColor: z.string().regex(HEX, 'Use a HEX color like #1A1A2E'),
  font: z.string().min(1),
  clientLogo: z.string().min(1, 'Logo is required'),
  brandLogo: z.string().nullable().optional(),
  backgroundImage: z.string().nullable().optional(),
  language: z.enum(['en', 'en-es']),
  collectData: z.boolean(),
  collectName: z.boolean(),
  collectEmail: z.boolean(),
  collectPhone: z.boolean(),
  ageGate: z.enum(['none', '18+', '21+']),
  demoAge: z.boolean(),
  demoGender: z.boolean(),
  demoEthnicity: z.boolean(),
  demoNationality: z.boolean(),
  gameMechanic: z.literal('wheel'),
  celebrationTheme: z.enum(['confetti', 'fireworks', 'sparks']),
});
export type BrandKitInput = z.infer<typeof brandKitSchema>;

/**
 * Onboarding asks for less than the full kit, and none of it is mandatory.
 *
 * Requiring a campaign name and a logo file to finish signup meant a brand
 * manager who had not yet been sent the assets could not get into the product
 * at all — the gate sat between them and every screen, on their first minute.
 * Every field here has a sane default and a permanent home in Brand Studio, so
 * the kit is something you refine once you are inside, not a toll to enter.
 *
 * The colours keep their HEX check: they are pre-filled, so a value that is
 * present but malformed is a real mistake worth catching.
 */
export const onboardingBrandKitSchema = brandKitSchema
  .pick({
    primaryColor: true,
    secondaryColor: true,
    font: true,
  })
  .extend({
    campaignName: z.string().optional(),
    clientLogo: z.string().optional(),
  });
export type OnboardingBrandKitInput = z.infer<typeof onboardingBrandKitSchema>;

export const GOOGLE_FONTS = [
  'Inter',
  'Roboto',
  'Montserrat',
  'Poppins',
  'Oswald',
  'Playfair Display',
  'Raleway',
  'Lato',
  'Open Sans',
  'Nunito',
  'Bebas Neue',
  'Archivo Black',
  'DM Sans',
  'Space Grotesk',
  'Outfit',
  'Sora',
  'Lexend',
  'Plus Jakarta Sans',
  'Urbanist',
  'Bricolage Grotesque',
] as const;
